// Zweck: Gemeinsame Datenverträge der Offline-Queue.
// Kontext: Persistierte Uploads, API-Änderungen und Synchronisierungsereignisse.
// Stand: 2026-10-05
export interface QueuedUploadFile {
  id?: string;
  name?: string;
  type?: string;
  size?: number;
  dataUrl: string;
}

/**
 * Persistierte Form von `QueuedUploadFile`: `dataUrl` (Base64) ist durch eine ID im
 * separaten `queueBlobStore` ersetzt (siehe queueService.ts, ADO #1216). Nur intern in
 * queueService.ts verwendet (`extractQueueBlobs`/`hydrateQueueBlobs`/`removeQueueBlobs`) -
 * Aufrufer von `addToQueue` (z.B. PhaseScreen.tsx) bauen weiterhin ganz normal
 * `QueuedUploadFile` mit `dataUrl`, die Umwandlung passiert transparent beim Einreihen.
 */
export interface QueuedUploadFileRef {
  id?: string;
  name?: string;
  type?: string;
  size?: number;
  blobRef: string;
}

export interface ProcessResult {
  successful: string[];
  failed: string[];
  /**
   * Teilmenge von `failed`, die endgültig aus der Queue entfernt wurde (Retries erschöpft
   * oder permanenter 4xx-Fehler) — im Unterschied zu einem Item, das nach einem transienten
   * Fehler mit erhöhtem Retry-Zähler weiterhin in der Queue liegt und später erneut
   * versucht wird. Aufrufer, die auf ein bestimmtes Item reagieren (z.B. ReworkCommentThread),
   * brauchen diese Unterscheidung, um nicht auf einen Kommentar zu reagieren, der im
   * Hintergrund noch synchronisiert wird.
   */
  permanentlyFailed: string[];
}

/** Kompakte Zuordnung eines synchronisierten Queue-Items für Status-Refreshes. */
export interface ProjectStatusRefreshItem {
  id: string;
  type: string;
  projectId?: string;
  protocolId?: string;
  /**
   * Echte, vom Server vergebene ID des durch dieses Queue-Item erzeugten Objekts (z.B. die
   * echte HubSpot-Kommentar-ID für `rework_comment_add`) — nur gesetzt, wenn der jeweilige
   * Queue-Item-Typ ein Ergebnis mit eigener ID zurückgibt (siehe processQueueItem in
   * queueService.ts). PR-Review-Finding zu #1090: eine nachträgliche Zuordnung "welcher
   * frisch geladene Datensatz gehört zu diesem Queue-Item" per Inhalt (z.B. Text+Autor) ist
   * nicht eindeutig — Aufrufer wie ReworkCommentThread.confirmSuccess brauchen die echte ID
   * direkt aus der Server-Antwort.
   */
  resultId?: string;
}

/** Detaildaten des globalen Refresh-Events nach einer erfolgreichen Queue-Synchronisierung. */
export interface ProjectStatusRefreshDetail {
  successfulIds: string[];
  successfulItems: ProjectStatusRefreshItem[];
}

/**
 * Detaildaten des globalen `queue-item-permanently-failed`-Events (siehe queueService.ts).
 * Trägt die vollen Item-Referenzen statt nur IDs, da das Item zum Dispatch-Zeitpunkt bereits
 * aus der Queue entfernt wurde — Aufrufer, die z.B. eine per `submittedAt` identifizierte
 * Payload gegen ihren aktuellen optimistischen State abgleichen müssen (siehe
 * AbschlussberichtWizard), brauchen dafür die ursprüngliche `data`.
 */
export interface QueueItemsPermanentlyFailedDetail {
  permanentlyFailedItems: QueueItem[];
}

/**
 * Detaildaten des globalen `queue-item-sync-finished`-Events (siehe queueService.ts). Feuert
 * für JEDES Item, dessen In-Flight-Status endet — Erfolg, permanenter Fehlschlag ODER
 * transienter Fehlschlag mit Retry — im Unterschied zu `ProjectStatusRefreshDetail` (nur
 * Erfolg) und `QueueItemsPermanentlyFailedDetail` (nur endgültiger Fehlschlag).
 */
export interface QueueItemsSyncFinishedDetail {
  finishedIds: string[];
}

export interface QueueItem {
  id: string;
  type: string;
  data: any;
  retries: number;
  createdAt: Date;
}

/**
 * Payload-Form für `addToQueue('document_upload', ...)`.
 * `addToQueue` selbst bleibt `data: any` (Queue trägt mehrere Item-Typen),
 * aber Aufrufer können ihr Objektliteral mit `satisfies DocumentUploadQueueData`
 * gegen diese Form prüfen lassen, damit Tippfehler bei Feldnamen auffallen.
 */
export interface DocumentUploadQueueData {
  imageData?: string;
  files?: QueuedUploadFile[];
  documentId: string;
  phaseId: string;
  projectId?: string;
  dealName: string;
  existingFiles?: unknown;
  sharedDrive?: string;
  gdriveFolderId?: string;
  /** Gescannte Seriennummer, z. B. für Seriennummer-Außeneinheit-Fotos (Dateiname `image-serial-no-<seriennummer>`). */
  serialNumber?: string;
  /**
   * Ausstehende HubSpot-Property-Patches, deren Drive-Upload bereits bestätigt ist, deren
   * eigenständiges `hubspot_text_property_update`-Retry-Item aber nicht eingereiht werden
   * konnte (z. B. IndexedDB-Quota beim `addQueueItem`-Aufruf selbst). Bleiben so am
   * `document_upload`-Item selbst retry-fähig, statt beim Enqueue-Fehler spurlos verloren
   * zu gehen (#1231-Re-Review-Finding).
   *
   * Array statt Einzelwert: bei einem Multi-Foto-Batch kann dieser Fall für mehrere Fotos
   * nacheinander eintreten (Foto A und Foto B verlieren beide ihr Retry-Item-Enqueue) - ein
   * einzelner Slot würde dann den Patch von A durch den von B überschreiben bzw. beim
   * erfolgreichen Enqueue von B den noch offenen Patch von A mitlöschen (#1231-Review-Finding,
   * Re-Review).
   */
  pendingHubspotPatch?: HubspotTextPropertyQueueData[];
  /**
   * Gesetzt, sobald der letzte Datei-Upload dieses Items bestätigt und `files` auf ein leeres
   * Array gekürzt wurde (siehe queueService.processDocumentUpload). Unterscheidet diesen
   * vollständig abgeschlossenen Zustand (App/Tab evtl. beendet, BEVOR processQueue das Item
   * final aus der Queue entfernen konnte) von einem genuinely unvollständigen/fehlerhaft
   * eingereihten Item, das ebenfalls mit leerer `files`-Liste ankäme (#1231-Re-Review-Finding,
   * dennis360volt).
   */
  allFilesUploaded?: boolean;
}

/**
 * Persistierte Form von `DocumentUploadQueueData` (und dem Legacy-Typ `image_upload`, der
 * dieselbe `imageData`/`files`-Struktur direkt in `item.data` trägt): `imageData`/
 * `files[].dataUrl` sind durch Blob-Store-Referenzen ersetzt (ADO #1216, siehe
 * queueService.ts `extractQueueBlobs`/`hydrateQueueBlobs`). Nur intern in queueService.ts
 * verwendet - Aufrufer bleiben unverändert bei `DocumentUploadQueueData`.
 */
export interface DocumentUploadQueuePersistedData
  extends Omit<DocumentUploadQueueData, "imageData" | "files"> {
  imageDataRef?: string;
  files?: QueuedUploadFileRef[];
}

/** Daten eines wiederholbaren Uploads von Serviceanhängen. */
export interface MaintenanceProtocolAttachmentQueueData {
  projectId: string;
  protocolId: string;
  dealName?: string;
  files: QueuedUploadFile[];
  payload?: import('./maintenance').MaintenanceProtocolUpsertPayload;
}

/** Daten einer vorgemerkten Finalisierung inklusive der PDF-Projektdaten. */
export interface MaintenanceProtocolFinalizeQueueData {
  projectId: string;
  protocolId: string;
  dealName?: string;
  projectDetails?: import('./maintenance').MaintenanceProtocolProjectDetails;
  payload?: import('./maintenance').MaintenanceProtocolUpsertPayload;
}

/**
 * Payload-Form für `addToQueue('hubspot_text_property_update', ...)`.
 * Für Fälle, in denen ein HubSpot-Text-Property direkt (ohne Datei-Upload)
 * geschrieben werden muss und der Live-Call fehlgeschlagen ist (z. B. offline).
 *
 * `multiUpload`/`newFiles`: bei Multi-Upload-Dokumenten (Property = JSON-Array
 * mehrerer Dateien) darf der Retry nicht blind den zum Enqueue-Zeitpunkt
 * berechneten `value`-Snapshot schreiben — zwischenzeitlich von anderen
 * Uploads/Geräten hinzugefügte Dateien gingen sonst verloren. Stattdessen liest
 * der Retry den aktuellen Property-Wert und merged `newFiles` dedupliziert hinein.
 */
export interface HubspotTextPropertyQueueData {
  /** Stabile Vorgangskennung vom Live-Aufruf; Alt-Einträge verwenden ihre Queue-ID. */
  idempotencyKey?: string;
  dealId: string;
  property: string;
  value: string;
  multiUpload?: boolean;
  newFiles?: import('../types/documents').UploadedDocumentFile[];
}

/**
 * Payload-Form für `addToQueue('vaillant_serial_register', ...)`.
 *
 * Fallback für PhaseScreen.handleSeriennummerRegistrierenSubmit: bei einem
 * Netzwerkfehler/Timeout/5xx während der Vaillant-Registrierung (nicht bei einer
 * fachlichen Ablehnung, `data.success === false` — die bleibt sofortiger Fehler ohne
 * Retry, da eine ungültige Seriennummer durch Wiederholen nicht gültig wird) wird die
 * Registrierung hier vorgemerkt, statt komplett verloren zu gehen (#1199 — der
 * Screenshot "Seriennummer konnte nicht registriert werden" zeigte genau diesen Fall,
 * für den es bisher — anders als bei der Außeneinheit-Seriennummer direkt darüber —
 * kein Queue-Fallback gab).
 */
export interface VaillantSerialRegisterQueueData {
  /**
   * Stabil auf `vaillant_serial_register:<dealId>` gesetzt (siehe
   * queueVaillantSerialRegisterFallback in PhaseScreen.tsx), damit ein erneuter fehlgeschlagener
   * Versuch das bestehende bestätigungspflichtige Item ERSETZT statt einen zweiten,
   * konkurrierenden Eintrag für denselben Deal anzulegen — sonst könnten zwei Banner/Items für
   * dieselbe Registrierung entstehen.
   */
  queueKey?: string;
  dealId: string;
  serialNumber: string;
  contactEmail?: string;
  inbetriebnahmePhaseId: string;
  /**
   * One360-API-Idempotency-Key (#1202) — einmal beim ursprünglichen Live-Versuch erzeugt
   * (siehe PhaseScreen.performSeriennummerRegistrierenSubmit) und bei jedem Retry aus dieser
   * Queue unverändert wiederverwendet. Ein wiederholter Registrierungsaufruf mit demselben Key
   * für dieselbe Seriennummer kontaktiert Vaillant serverseitig nicht erneut.
   */
  idempotencyKey: string;
}

/**
 * Queue-Item-Typen, die NICHT automatisch im Hintergrund verarbeitet werden dürfen —
 * nur nach expliziter Nutzerbestätigung über QueueContext.confirmPendingItem.
 *
 * Hintergrund (#1199, Code-Review-Finding): ein Timeout/Verbindungsabbruch bei der
 * Vaillant-Registrierung beweist NICHT, dass Vaillant die Anfrage nicht doch verarbeitet
 * hat — nur die Antwort kann verloren gegangen sein. Ein automatischer Retry im
 * Hintergrund (beim nächsten Reconnect, App-Start oder "Sync jetzt") würde in diesem Fall
 * `registerVaillantSerialNumber` ein zweites Mal aufrufen und könnte eine zweite
 * Registrierung/Bestätigungsmail an den Kunden auslösen. Ohne einen serverseitigen
 * Idempotency-Key (siehe Backend-Ticket für die One360-API) kann das Frontend nicht sicher
 * automatisch retryen — deshalb bleibt ein Item dieses Typs in der Queue liegen, bis der
 * Monteur explizit bestätigt (z.B. nachdem er geprüft hat, ob der Kunde bereits eine
 * Bestätigungsmail erhalten hat).
 */
export const MANUAL_CONFIRMATION_QUEUE_TYPES: readonly string[] = ['vaillant_serial_register'];

export const requiresManualConfirmation = (type: string): boolean =>
  MANUAL_CONFIRMATION_QUEUE_TYPES.includes(type);

/**
 * Payload-Form für `addToQueue('rework_comment_add', ...)`.
 * Deckt sowohl das Neu-Anlegen als auch das spätere Ändern eines noch nicht
 * synchronisierten Kommentars ab: `queueKey` wird beim Anlegen auf die lokale
 * `pending-<timestamp>`-Kommentar-ID gesetzt, wodurch die IndexedDB-Queue-Item-ID mit ihr
 * übereinstimmt. Ein erneuter addToQueue-Aufruf mit demselben `queueKey` überschreibt
 * dasselbe Item (bestehendes addToQueue-Verhalten, siehe QueueContext.tsx) — das nutzt
 * ReworkCommentThread.handleSaveEdit, um einen noch wartenden Kommentar lokal zu ändern,
 * ohne einen API-Call oder eine echte HubSpot-Kommentar-ID zu brauchen. Bearbeiten/Löschen
 * eines bereits SYNCHRONISIERTEN Kommentars laufen dagegen weiterhin online-only direkt
 * gegen die API (siehe ReworkCommentThread).
 */
export interface ReworkCommentAddQueueData {
  /** Nur beim (Re-)Enqueuen relevant, kein Server-Feld — siehe addToQueue in QueueContext.tsx. */
  queueKey?: string;
  ticketId: string;
  text: string;
  mentionedOwnerIds: string[];
}

/**
 * Ergebnis eines sofort (synchron zum addToQueue-Aufruf) verarbeiteten Items —
 * `undefined`, wenn die Verarbeitung nicht sofort lief (offline, gerade läuft schon eine
 * andere Verarbeitung, o.ä.) und das Item stattdessen nur in der Queue registriert wurde.
 * Ohne dieses Ergebnis lässt sich aus bloßem Nichtvorhandensein in `items` nicht zuverlässig
 * ableiten, ob ein Item noch gar nicht registriert oder schon fertig verarbeitet wurde
 * (PR-Review-Finding zu Ticket #1090, siehe ReworkCommentThread.tsx). Identisch zu
 * `ProcessResult` — eigener Name, weil er aus Aufrufer-Sicht (addToQueue) etwas anderes
 * bedeutet als aus queueService-Sicht (Ergebnis eines ganzen Batch-Laufs).
 */
export type QueueProcessOutcome = ProcessResult;

export interface QueueContextType {
  items: QueueItem[];
  addToQueue: (type: string, data: any) => Promise<QueueProcessOutcome | undefined>;
  /**
   * Entfernt tatsächlich async (Promise<void>) - die Signatur wurde von `void` korrigiert
   * (Code-Review-Finding zu #1199): removeQueueItem kann jetzt bei einem Persistenzfehler
   * werfen, Aufrufer müssen das awaiten/catchen können können, statt fire-and-forget
   * aufzurufen und einen Fehlschlag stillschweigend zu verpassen.
   */
  removeFromQueue: (id: string) => Promise<void>;
  /**
   * Entfernt ein Item NUR aus dem React-State, ohne erneut in IndexedDB zu schreiben/löschen -
   * für Aufrufer, die eine Entfernung bereits selbst atomar außerhalb dieses Contexts
   * durchgeführt haben (z.B. queueService.removeStaleQueueItem) und nur noch Badge/Queue-Liste
   * synchron halten wollen. Ein erneuter `removeFromQueue`-Aufruf dafür wäre ein zweiter,
   * ungeschützter IndexedDB-Zugriff außerhalb des ursprünglichen Locks und könnte einen
   * zwischenzeitlich für dieselbe ID neu eingereihten, nie gesendeten Payload mitlöschen
   * (PR-Review-Finding, 4. Re-Review zu #976).
   */
  forgetQueueItem: (id: string) => void;
  /**
   * Verarbeitet EIN bestimmtes Queue-Item explizit auf Nutzeranfrage, unabhängig davon, ob
   * sein Typ von der automatischen Verarbeitung ausgenommen ist (siehe
   * MANUAL_CONFIRMATION_QUEUE_TYPES). `undefined`, wenn offline, bereits ein Sync läuft, oder
   * das Item nicht (mehr) in der Queue existiert.
   */
  confirmPendingItem: (id: string) => Promise<QueueProcessOutcome | undefined>;
  isProcessing: boolean;
  isOnline: boolean;
  syncQueue: () => Promise<void>;
  syncStatus: {
    pending: number;
    lastSync: Date | null;
    error: string | null;
    state: import('../types/sync-state').SyncState;
    lastResult?: { successful: number; failed: number };
  };
}
